// Marketing tracking for the app: data-layer pushes for GTM (GA4 + Meta Pixel)
// and the server copy of each conversion for the Meta Conversions API.
// Spec: DEV_02 "Tracking setup for gncreators.com" §5 and §7.
//
// Three rules hold for everything in this file:
//   1. Tracking never breaks a user flow. Every export swallows its own errors
//      and the backend call is fire-and-forget: nothing here is awaited by a
//      caller, and nothing is shown to the user.
//   2. No raw PII (email, phone, name) goes into the data layer. The server
//      looks the user up from the auth token.
//   3. Each conversion carries an `event_id`; the browser push and the server
//      call use the same one so Meta counts the event once.
//
// Kept free of axios, React and redux on purpose: src/index.js imports it to
// capture attribution and install the WhatsApp listener, and index.js is the
// tiny main.js that paints before the app's own chunks load.

import { getSelfToken } from '../../_helpers/auth/secureToken';

export const ATTR_COOKIE = 'gnc_attr';
export const ATTR_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'seg', 'fbclid'];
const ATTR_MAX_AGE = 2592000; // 30 days, same as the marketing site

// Must match public/index.html (and the GNLinks dashboard).
const CONSENT_POLICY_VERSION = '1.0';
const CONSENT_REASK_MS = 180 * 24 * 60 * 60 * 1000;

const TRACK_PATH = 'api/marketing/track/';
// sign_up fires the moment the register API succeeds; the Knox token arrives
// a moment later from the login that follows. Wait for it this long.
const TOKEN_WAIT_TRIES = 30;
const TOKEN_WAIT_MS = 1000;

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

// ── Ids ────────────────────────────────────────────────────────────────────

export function newEventId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) { /* fall through */ }
  // RFC 4122 v4 shape from Math.random — only for old browsers / insecure origins.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── Cookies ────────────────────────────────────────────────────────────────

export function readCookie(name) {
  try {
    if (!hasWindow()) return null;
    const prefix = `${name}=`;
    const part = document.cookie.split(/;\s*/).find((p) => p.indexOf(prefix) === 0);
    return part === undefined ? null : part.slice(prefix.length);
  } catch (e) {
    return null;
  }
}

const safeDecode = (value) => {
  try { return decodeURIComponent(value); } catch (e) { return value; }
};

// `.gncreators.com` so the marketing site and the app share it; host-only on
// localhost and on any other host (previews), where that domain would be refused.
export function attributionCookieDomain(hostname) {
  const host = String(hostname || '').toLowerCase();
  if (host === 'gncreators.com' || host.endsWith('.gncreators.com')) return '.gncreators.com';
  return '';
}

// ── Attribution (gnc_attr) ─────────────────────────────────────────────────

export function getAttribution() {
  try {
    const raw = readCookie(ATTR_COOKIE);
    if (!raw) return {};
    let text = safeDecode(raw);
    // Tolerate a value encoded twice.
    if (text && text.charAt(0) === '%') text = safeDecode(text);
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
}

// Reads utm_* / seg / fbclid from a query string. Null when none is present.
export function attributionFromSearch(search, href, now = Date.now()) {
  const params = new URLSearchParams(search || '');
  const found = {};
  ATTR_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) found[key] = value;
  });
  if (!Object.keys(found).length) return null;
  if (found.fbclid) found.fbclid_ts = now;
  found.landing_url = href || '';
  found.ts = now;
  return found;
}

const writeAttributionCookie = (value, maxAge) => {
  const domain = attributionCookieDomain(window.location.hostname);
  const secure = window.location.protocol === 'https:';
  const attrs = `; path=/; max-age=${maxAge}; SameSite=Lax` + (secure ? '; Secure' : '');
  document.cookie = `${ATTR_COOKIE}=${value}${attrs}${domain ? `; domain=${domain}` : ''}`;
  // A delete also clears any host-only copy written on this host.
  if (maxAge === 0 && domain) document.cookie = `${ATTR_COOKIE}=${attrs}`;
};

// The URL the app was opened on: a redirect (login, onboarding) can drop the
// campaign params before consent is given, and they are captured from here then.
let landingHref = null;

// gnc_attr is written only while analytics OR advertising consent is granted
// (the marketing site follows the same rule). Reading it is always fine.
// When the app's own URL carries campaign params they replace the cookie (last
// touch wins, as on the marketing site).
export function captureAttributionFromUrl(href) {
  try {
    if (!hasWindow()) return null;
    if (landingHref === null) landingHref = window.location.href;
    if (!attributionConsentGranted()) return null;
    const url = new URL(href || window.location.href, window.location.href);
    const attr = attributionFromSearch(url.search, url.href);
    if (!attr) return null;
    writeAttributionCookie(encodeURIComponent(JSON.stringify(attr)), ATTR_MAX_AGE);
    return attr;
  } catch (e) {
    return null;
  }
}

export function deleteAttributionCookie() {
  try {
    if (hasWindow()) writeAttributionCookie('', 0);
  } catch (e) { /* noop */ }
}

// ── Meta browser ids ───────────────────────────────────────────────────────

export function getFbp() {
  const value = readCookie('_fbp');
  return value ? safeDecode(value) : null;
}

export function getFbc() {
  const value = readCookie('_fbc');
  if (value) return safeDecode(value);
  const attr = getAttribution();
  if (attr.fbclid) return `fb.1.${attr.fbclid_ts || Date.now()}.${attr.fbclid}`;
  return null;
}

// ── Consent ────────────────────────────────────────────────────────────────

// The GNLinks record (written by gnTracker.js): cookie `gnl_consent`
// = encodeURIComponent(JSON {v, p: {analytics, advertising, identity}, ts, s}),
// mirrored to localStorage under the same key; the cookie is read first. Each
// purpose is "granted" | "denied" | "withdrawn"; only "granted" (or true)
// counts. Same rules as public/index.html: a wrong policy version or an expired
// decision is no decision (null).
export function readRawConsent() {
  try {
    if (!hasWindow()) return null;
    const cookie = readCookie('gnl_consent');
    let raw = cookie ? safeDecode(cookie) : null;
    if (!raw) {
      try { raw = window.localStorage.getItem('gnl_consent'); } catch (e) { raw = null; }
    }
    return raw || null;
  } catch (e) {
    return null;
  }
}

export function parseConsent(raw) {
  try {
    if (!raw) return null;
    const r = JSON.parse(raw);
    if (!r || !r.p || typeof r.p !== 'object') return null;
    if ((r.v || '1.0') !== CONSENT_POLICY_VERSION) return null;
    if (!r.ts || Date.now() - r.ts > CONSENT_REASK_MS) return null;
    const granted = (v) => v === 'granted' || v === true;
    return { analytics: granted(r.p.analytics), advertising: granted(r.p.advertising) };
  } catch (e) {
    return null;
  }
}

export function readStoredConsent() {
  return parseConsent(readRawConsent());
}

export function attributionConsentGranted() {
  const c = readStoredConsent();
  return !!(c && (c.analytics || c.advertising));
}

// Keeps gnc_attr in step with consent: captured when consent turns granted
// (from the current URL, else the URL the app was opened on), deleted when it
// turns denied/withdrawn. gnTracker.js fires no event on a change, so this
// runs on every click and on a 2s poll, and acts only when the raw record changed.
let lastConsentRaw;
export function syncAttributionConsent() {
  try {
    const raw = readRawConsent();
    if (raw === lastConsentRaw) return;
    lastConsentRaw = raw;
    const c = parseConsent(raw);
    if (!c) return; // no (current) decision: neither write nor delete
    if (c.analytics || c.advertising) {
      if (!captureAttributionFromUrl() && landingHref) captureAttributionFromUrl(landingHref);
    } else {
      deleteAttributionCookie();
    }
  } catch (e) { /* noop */ }
}

let consentWatchInstalled = false;
export function watchConsentForAttribution(intervalMs = 2000) {
  try {
    if (consentWatchInstalled || !hasWindow()) return;
    consentWatchInstalled = true;
    syncAttributionConsent();
    document.addEventListener('click', syncAttributionConsent, true);
    window.addEventListener('gnlinks:ready', syncAttributionConsent);
    setInterval(syncAttributionConsent, intervalMs);
  } catch (e) { /* noop */ }
}

export function hasAdConsent() {
  const c = readStoredConsent();
  return !!(c && c.advertising);
}

// ── Data layer ─────────────────────────────────────────────────────────────

export function pushDL(obj) {
  try {
    if (!hasWindow() || !obj) return;
    window.dataLayer = window.dataLayer || [];
    // GA4 merges ecommerce objects across pushes unless the previous one is cleared.
    if (Object.prototype.hasOwnProperty.call(obj, 'ecommerce')) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(obj);
  } catch (e) { /* never break the caller */ }
}

// ── Server copy (POST /api/marketing/track/) ───────────────────────────────

const isEmpty = (v) => v === undefined || v === null || v === '';

const omitEmpty = (obj) => Object.keys(obj).reduce((acc, key) => {
  if (!isEmpty(obj[key])) acc[key] = obj[key];
  return acc;
}, {});

const readToken = () => {
  try {
    const token = getSelfToken();
    return token && token !== 'null' && token !== 'undefined' ? token : null;
  } catch (e) {
    return null;
  }
};

export function trackUrl(base = process.env.REACT_APP_BASEURL) {
  const b = String(base || '');
  return `${b}${b && !b.endsWith('/') ? '/' : ''}${TRACK_PATH}`;
}

export function buildTrackBody(event, eventId, extra = {}) {
  const attr = getAttribution();
  const adConsent = hasAdConsent();
  const body = {
    event,
    event_id: eventId,
    ad_consent: adConsent,
    page_url: hasWindow() ? window.location.href : '',
    utm_source: attr.utm_source,
    utm_medium: attr.utm_medium,
    utm_campaign: attr.utm_campaign,
    utm_content: attr.utm_content,
    utm_term: attr.utm_term,
    seg: attr.seg,
    ...(extra || {}),
  };
  // Declined advertising: the event still goes, without the Meta browser ids.
  if (adConsent) {
    body.fbp = getFbp();
    body.fbc = getFbc();
  } else {
    delete body.fbp;
    delete body.fbc;
  }
  return omitEmpty(body);
}

const postTrack = (body, token) => {
  try {
    if (typeof fetch !== 'function') return;
    fetch(trackUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
      body: JSON.stringify(body),
      // Survives a navigation that starts right after the call.
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* fire-and-forget */ }
};

// Fire-and-forget. Returns nothing a caller could await. `options.token`
// overrides the stored session token; without either it waits briefly for the
// session to appear (sign-up), then gives up silently.
export function sendTrack(event, eventId, extra = {}, options = {}) {
  try {
    const body = buildTrackBody(event, eventId, extra);
    const tries = options.tries === undefined ? TOKEN_WAIT_TRIES : options.tries;
    const waitMs = options.waitMs === undefined ? TOKEN_WAIT_MS : options.waitMs;
    let attempt = 0;
    const go = () => {
      try {
        const token = options.token || readToken();
        if (token) {
          postTrack(body, token);
          return;
        }
        attempt += 1;
        if (attempt < tries) setTimeout(go, waitMs);
      } catch (e) { /* fire-and-forget */ }
    };
    go();
  } catch (e) { /* never break the caller */ }
}

// ── Event helpers (one call per success point) ─────────────────────────────

// How the Firebase account was created: google | phone | email.
export function signupMethodOf(user) {
  try {
    const ids = ((user && user.providerData) || []).map((p) => p && p.providerId).filter(Boolean);
    if (ids.includes('google.com')) return 'google';
    if (ids.includes('password')) return 'email';
    if (ids.includes('phone')) return 'phone';
    if (user && user.phoneNumber && !user.email) return 'phone';
  } catch (e) { /* default below */ }
  return 'email';
}

// After the register API has returned success (account actually created).
// Guarded per Firebase uid so two tabs finishing the same sign-up count once.
// `token`: the Knox token the register response carries; used straight away.
// Without it the server copy waits for the session token (fallback).
export function trackSignUp({ user, accountType, method, token } = {}) {
  try {
    const uid = user && user.uid;
    const guardKey = uid ? `gnc_signup_tracked_${uid}` : null;
    if (guardKey) {
      try {
        if (window.localStorage.getItem(guardKey)) return;
        window.localStorage.setItem(guardKey, '1');
      } catch (e) { /* storage blocked: track anyway */ }
    }
    const resolvedMethod = method || signupMethodOf(user);
    const resolvedType = accountType === 'brand' ? 'brand' : 'creator';
    const id = newEventId();
    pushDL(omitEmpty({
      event: 'sign_up',
      event_id: id,
      method: resolvedMethod,
      account_type: resolvedType,
      seg: getAttribution().seg,
    }));
    sendTrack('sign_up', id, { method: resolvedMethod, account_type: resolvedType }, token ? { token } : {});
  } catch (e) { /* never break sign-up */ }
}

// An Instagram connection reached READY. Once per user per browser; the server
// sends ActivateAccount itself, so there is no backend call.
export function trackInstagramConnected(userId) {
  try {
    const key = `gnc_ig_connected_tracked_${userId || 'anon'}`;
    try {
      if (window.localStorage.getItem(key)) return;
      window.localStorage.setItem(key, '1');
    } catch (e) { /* storage blocked: track anyway */ }
    pushDL({ event: 'instagram_connected', event_id: newEventId() });
  } catch (e) { /* never break the page */ }
}

const toMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// The order amount EXCLUDING GST, in rupees, from the subscribe (create-order)
// response: total_amount - gst_amount (both strings). Falls back to
// original_amount - discount_amount, then to the plan's price.
export function netAmountOf(order = {}, plan = {}) {
  const round = (n) => Math.round(n * 100) / 100;
  const explicit = toMoney(order && order.final_amount);
  if (explicit !== null) return round(explicit);
  const total = toMoney(order && order.total_amount);
  const gst = toMoney(order && order.gst_amount);
  if (total !== null && gst !== null) return round(total - gst);
  const original = toMoney(order && order.original_amount);
  if (original !== null) return round(original - (toMoney(order.discount_amount) || 0));
  const price = toMoney(plan && plan.price);
  return price !== null ? round(price) : 0;
}

export function planItems(plan = {}) {
  const p = plan || {};
  const id = p.id !== undefined && p.id !== null ? String(p.id) : p.slug;
  return [omitEmpty({ item_id: id, item_name: p.name })];
}

// Razorpay Checkout has just opened for this order.
export function trackBeginCheckout({ order = {}, plan = {} } = {}) {
  try {
    const id = newEventId();
    const thePlan = (plan && Object.keys(plan).length ? plan : order && order.plan) || {};
    pushDL({
      event: 'begin_checkout',
      event_id: id,
      ecommerce: {
        currency: 'INR',
        value: netAmountOf(order, thePlan),
        items: planItems(thePlan),
      },
    });
    sendTrack('begin_checkout', id, { order_id: order && order.order_id });
  } catch (e) { /* never break checkout */ }
}

// The payment was verified. The server sends Purchase/Subscribe from the
// payment record, with the same payment id as event_id, so no backend call.
export function trackPurchase({ paymentId, order = {}, plan = {} } = {}) {
  try {
    if (!paymentId) return;
    const thePlan = (plan && Object.keys(plan).length ? plan : order && order.plan) || {};
    pushDL({
      event: 'purchase',
      event_id: paymentId,
      ecommerce: {
        transaction_id: paymentId,
        value: netAmountOf(order, thePlan),
        currency: 'INR',
        items: planItems(thePlan),
      },
    });
  } catch (e) { /* never break the success screen */ }
}

// ── WhatsApp clicks ────────────────────────────────────────────────────────

const WHATSAPP_HREF = /^(?:whatsapp:|(?:https?:)?\/\/(?:[a-z0-9-]+\.)*(?:wa\.me|whatsapp\.com)(?:[/:?#]|$))/i;

export const isWhatsAppHref = (href) => WHATSAPP_HREF.test(String(href || ''));

let whatsappInstalled = false;

export function handleWhatsAppClick(e) {
  try {
    const start = e && e.target;
    const link = start && typeof start.closest === 'function' ? start.closest('a[href]') : null;
    if (!link) return;
    const href = link.href || link.getAttribute('href');
    if (!isWhatsAppHref(href)) return;
    pushDL({ event: 'whatsapp_click', link_url: href, click_location: window.location.pathname });
  } catch (err) { /* never block the click */ }
}

// One delegated listener for the whole app (capture phase, so a handler that
// stops propagation cannot hide the click).
export function installWhatsAppClickTracking() {
  try {
    if (whatsappInstalled || !hasWindow()) return;
    document.addEventListener('click', handleWhatsAppClick, true);
    whatsappInstalled = true;
  } catch (e) { /* noop */ }
}

// App start: src/index.js.
export function initTracking() {
  captureAttributionFromUrl();
  watchConsentForAttribution();
  installWhatsAppClickTracking();
}
